// RecordRow (job page round 2, Amir 26 Oct: "align the sections to one grid"; "every item clickable to
// its detail"): one row in a record's list (a visit, a filled form) that opens its detail. One grid for
// every such list: a when column (the same width as History's time column), the row's line and a muted
// line under it, and an end column for its state word or an amount. No box and no chevron: the whole
// row is the button, shaded on hover, ringed on keyboard focus, and its rule is a hairline under it.
// A row the page was opened from says so ("You came from this visit"). Its columns are History's
// (HistoryItem): the gutter is History's time column ("Visit 2", "11:45 a.m."), then a glyph column, so
// a section's lines start where History's do and the page reads down one edge.
// Round 3, opens in place (Amir 26 Oct: "the same as History, each item expands in place"): with
// onToggle the row is a disclosure drawn as HistoryItem's: the whole row one button with aria-expanded
// and aria-controls, Area's chevron at its end, turned down when open; open, the ink mark down its left
// edge and its entry (`expanded`, an EntryDetail inline) under its words. Escape inside an open row
// closes it and puts the focus back on its row; in a RecordList ↑ ↓ move between the rows, Home and End
// to the first and last; the disclosure is HistoryItem's (row-disclosure.tsx). Reads only --rrow-*.
import { Children, isValidElement, type KeyboardEvent, type ReactNode } from 'react';
import { Glyph, type GlyphName } from '../Glyph/Glyph.tsx';
import { DisclosureChev, DisclosureEntry, useDisclosure } from '../row-disclosure.tsx';
import { StateMark } from '../StateMark/StateMark.tsx';
import './RecordRow.css';

export interface RecordRowProps {
  /** The line: "Mon Oct 26 · 8 a.m.–4 p.m. · Sam and Lee". */
  title: ReactNode;
  /** Under it, muted: what it was for, how it ended. */
  sub?: ReactNode;
  /** The gutter (History's time column): "Visit 2", "3:30 p.m."; none and the line starts at the edge. */
  when?: ReactNode;
  whenSub?: ReactNode;
  /** At the end: the state word (with its mark), or an amount. */
  end?: ReactNode;
  /** Under the end, muted. */
  endSub?: ReactNode;
  /** A problem with it, under the line in ink after StateMark's red bar ("Permit number: blank"). */
  problem?: ReactNode;
  /** Opens its detail; without it the row is read only. */
  onOpen?: () => void;
  /** The button's name for screen readers, when the words alone don't say what opens. */
  label?: string;
  /** The page was opened from this row (a visit on the board). */
  cameFrom?: string;
  /** Its detail is open now. */
  isOpen?: boolean;
  id?: string;
  /** In History's glyph column, for a list that mixes kinds (a form, the signature). */
  glyph?: GlyphName;
  /** Opens in place: the row is a disclosure, its entry (`expanded`) under it. */
  onToggle?: () => void;
  /** The open row's entry (EntryDetail, placement inline). */
  expanded?: ReactNode;
}

export function RecordRow({
  title,
  sub,
  when,
  whenSub,
  end,
  endSub,
  problem,
  onOpen,
  label,
  cameFrom,
  isOpen,
  id,
  glyph,
  onToggle,
  expanded,
}: RecordRowProps) {
  const { head, onKeyDown } = useDisclosure(!!isOpen, onToggle);
  const inside = (
    <>
      {when !== undefined ? (
        <span className="fl-rrow__when">
          <span>{when}</span>
          {whenSub ? <span className="fl-rrow__muted">{whenSub}</span> : null}
        </span>
      ) : null}
      {when !== undefined ? (
        <span className="fl-rrow__kind" aria-hidden="true">
          {glyph ? <Glyph name={glyph} className="fl-rrow__glyph" /> : null}
        </span>
      ) : null}
      <span className="fl-rrow__main">
        <span className="fl-rrow__title">{title}</span>
        {sub ? <span className="fl-rrow__sub">{sub}</span> : null}
        {problem ? (
          <span className="fl-rrow__bad">
            <StateMark state="problem" />
            {problem}
          </span>
        ) : null}
        {cameFrom ? <span className="fl-rrow__came">{cameFrom}</span> : null}
      </span>
      {end !== undefined ? (
        <span className="fl-rrow__end">
          <span>{end}</span>
          {endSub ? <span className="fl-rrow__muted">{endSub}</span> : null}
        </span>
      ) : null}
    </>
  );
  const at = when !== undefined ? 'when' : undefined;
  if (onToggle) {
    const open = !!isOpen;
    const entryId = `${id ?? 'row'}-entry`;
    return (
      <div id={id} className="fl-rrow-item" data-open={open ? '' : undefined} onKeyDown={onKeyDown}>
        <button
          ref={head}
          type="button"
          className="fl-rrow"
          data-when={at}
          data-toggle=""
          aria-label={label}
          aria-expanded={open}
          aria-controls={entryId}
          onClick={onToggle}
        >
          {inside}
          <DisclosureChev className="fl-rrow__chev" open={open} />
        </button>
        <DisclosureEntry
          className="fl-rrow__entry"
          id={entryId}
          label={label ?? (typeof title === 'string' ? title : undefined)}
          open={open}
        >
          {expanded}
        </DisclosureEntry>
      </div>
    );
  }
  return onOpen ? (
    <button
      type="button"
      id={id}
      className="fl-rrow"
      data-when={at}
      data-open={isOpen ? '' : undefined}
      aria-label={label}
      aria-haspopup="dialog"
      onClick={onOpen}
    >
      {inside}
    </button>
  ) : (
    <div id={id} className="fl-rrow" data-when={at} data-static="">
      {inside}
    </div>
  );
}

/** ↑ ↓ between a list's rows that open in place, past an open entry's links; Home and End to the ends. */
function onListKey(ev: KeyboardEvent<HTMLDivElement>) {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(ev.key)) return;
  const t = ev.target as HTMLElement;
  if (!t.matches('.fl-rrow[data-toggle]')) return;
  const heads = [...ev.currentTarget.querySelectorAll<HTMLElement>('.fl-rrow[data-toggle]')];
  const i = heads.indexOf(t);
  const j =
    ev.key === 'ArrowDown'
      ? Math.min(i + 1, heads.length - 1)
      : ev.key === 'ArrowUp'
        ? Math.max(i - 1, 0)
        : ev.key === 'Home'
          ? 0
          : heads.length - 1;
  ev.preventDefault();
  heads[j]?.focus();
}

/** A list of RecordRows, named for screen readers ("Visits"). */
export function RecordList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="fl-rrow-list" role="list" aria-label={label} onKeyDown={onListKey}>
      {Children.toArray(children).map((c, i) => (
        <div
          key={isValidElement(c) && c.key != null ? c.key : i}
          role="listitem"
          className="fl-rrow-list__item"
        >
          {c}
        </div>
      ))}
    </div>
  );
}
